
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CheckCheck,
  FileText,
  Calendar,
  User,
  Briefcase,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { useNotifications } from "@/hooks/use-notifications";

const NotificationItem = ({
  notification,
  onRead,
  onClick,
}: {
  notification: any;
  onRead: (id: string) => void;
  onClick: (notification: any) => void;
}) => {
  const getNotificationIcon = (type: string) => {
    if (type.includes("mfo")) return <FileText className="h-5 w-5" />;
    if (type.includes("deadline")) return <Clock className="h-5 w-5 text-amber-500" />;
    if (type.includes("task") || type.includes("kanban"))
      return <FileText className="h-5 w-5 text-blue-500" />;
    if (type.includes("employee")) return <User className="h-5 w-5 text-green-500" />;
    if (type.includes("department")) return <Briefcase className="h-5 w-5 text-purple-500" />;
    return <Bell className="h-5 w-5" />;
  };

  return (
    <div
      className={`border p-4 rounded-lg mb-3 cursor-pointer hover:bg-accent/50 ${
        !notification.is_read ? "bg-accent/20" : ""
      }`}
      onClick={() => onClick(notification)}
    >
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-muted p-2">
          {getNotificationIcon(notification.type)}
        </div>
        <div className="flex-1">
          <div className="flex justify-between items-start">
            <h4 className="font-medium text-sm">{notification.title}</h4>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(notification.created_at), {
                  addSuffix: true,
                })}
              </span>
              {!notification.is_read && (
                <span className="h-2 w-2 rounded-full bg-primary"></span>
              )}
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {notification.message}
          </p>
          {!notification.is_read && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 px-2 mt-2"
              onClick={(e) => {
                e.stopPropagation();
                onRead(notification.id);
              }}
            >
              Mark as read
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const Notifications = () => {
  const [activeTab, setActiveTab] = useState("all");
  const { 
    notifications, 
    unreadCount, 
    isLoading, 
    markAsRead, 
    markAllAsRead 
  } = useNotifications();
  const navigate = useNavigate();

  const handleNotificationClick = (notification: any) => {
    // Mark as read
    markAsRead(notification.id);

    // Navigate based on notification type
    if (notification.type.includes("mfo")) {
      navigate("/dashboard/mfo");
    } else if (
      notification.type.includes("task") ||
      notification.type.includes("kanban")
    ) {
      navigate("/dashboard/kanban");
    } else if (notification.type.includes("employee")) {
      navigate("/dashboard/employees");
    } else if (notification.type.includes("department")) {
      navigate("/dashboard/divisions");
    }
  };

  const filteredNotifications = notifications.filter((notification) => {
    if (activeTab === "all") return true;
    if (activeTab === "unread") return !notification.is_read;
    if (activeTab === "deadlines") return notification.type.includes("deadline");
    if (activeTab === "tasks")
      return (
        notification.type.includes("task") ||
        notification.type.includes("kanban")
      );
    if (activeTab === "mfo") return notification.type.includes("mfo");
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Notifications</h2>
          <p className="text-muted-foreground">
            View and manage your notifications
          </p>
        </div>
        {unreadCount > 0 && (
          <Button onClick={() => markAllAsRead()}>
            <CheckCheck className="mr-2 h-4 w-4" />
            Mark all as read
          </Button>
        )}
      </div>
      
      <Card>
        <CardHeader>
          <Tabs
            defaultValue="all"
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="all">
                All
                <Badge
                  variant="secondary"
                  className="ml-2 px-1.5 py-0.5 text-xs"
                >
                  {notifications.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="unread">
                Unread
                {unreadCount > 0 && (
                  <Badge
                    variant="default"
                    className="ml-2 px-1.5 py-0.5 text-xs"
                  >
                    {unreadCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="deadlines">Deadlines</TabsTrigger>
              <TabsTrigger value="tasks">Tasks</TabsTrigger>
              <TabsTrigger value="mfo">MFO</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="h-12 w-12 mx-auto text-muted-foreground/70" />
              <h3 className="mt-4 text-lg font-medium">No notifications</h3>
              <p className="text-muted-foreground">
                You don't have any{" "}
                {activeTab !== "all" ? `${activeTab} ` : ""}notifications.
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onRead={markAsRead}
                  onClick={handleNotificationClick}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Notifications;
